import { FC, useState, useEffect, useRef } from 'react';
import { useAppSelector, useEnhancedTranslation } from '@app/hooks';
import './order.tab.scss';
import { formatDate, formatNumberWithSpaces } from '@app/utils';
import { cancelOrder, getOrderListData, paymentOrder, TOrderListItem } from '@app/api/orders/orders.service';
import { NewButton } from '@app/components/NewButton/NewButton';
import OrderTabComponent from './components/OrderTabComponent/OrderTabComponent';
import { getOrderListItemSum, getOrderTabComponentStatus } from './helpers';

export const OrderTab: FC = () => {
  const { t } = useEnhancedTranslation({ keyPrefix: 'account.orders' });
  const prevPageRef = useRef(1);
  const isInitialMount = useRef(true);
  const [orderData, setOrderData] = useState<TOrderListItem[]>([]);
  const [page, setPage] = useState(1);
  const [isShowMoreVisible, setIsShowMoreVisible] = useState(false);
  const {
    authReducer: {
      access: { accessToken },
    },
  } = useAppSelector((state) => state);
  async function getData(currentPage: number, token?: string): Promise<void> {
    try {
      const res = await getOrderListData(currentPage - 1, token);
      if (res.totalCount > currentPage * 10) {
        setIsShowMoreVisible(true);
      } else {
        setIsShowMoreVisible(false);
      }
      if (currentPage !== 1) {
        setOrderData((prev) => [...prev, ...res.data]);
      } else {
        setOrderData(res.data);
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function onCancelBtn(id: string, token: string): Promise<void> {
    try {
      await cancelOrder(id, token);
    } catch (error) {
      console.log(error);
    } finally {
      setPage(1);
      getData(1, accessToken || '');
    }
  }

  async function onPaymentBtn(id: string, token: string): Promise<void> {
    try {
      const res = await paymentOrder(id, token);
      if (res?.invoice_url) {
        window.location.href = res?.invoice_url;
      }
    } catch (error) {
      console.log(error);
    } finally {
      setPage(1);
      getData(1, accessToken || '');
    }
  }

  useEffect(() => {
    if (isInitialMount.current && accessToken) {
      // Первый рендер (даже если Strict Mode вызывает его дважды)
      getData(page, accessToken || '');
      isInitialMount.current = false;
    } else if (prevPageRef.current !== page && accessToken) {
      getData(page, accessToken || '');
    }
    prevPageRef.current = page;
  }, [accessToken, page]);

  return (
    <main className="order-tab">
      <h1 className="order-tab__title">{t('title')}</h1>
      <div className="order-tab__wrap">
        {orderData?.map((item) => (
          <OrderTabComponent
            key={item?._id}
            id={item?._id}
            titleNumber={item?.orderNumber?.toString()}
            date={formatDate(new Date(item?.createdAt))}
            status={getOrderTabComponentStatus(item.status)}
            price={`${formatNumberWithSpaces(getOrderListItemSum(item).toString())} ₽`}
            cards={item.items.map((card) => {
              return {
                id: card.product?._id,
                image: `${process.env.REACT_APP_IMAGES_URL}/${card.product.images[0]?.url}`,
                price: `${formatNumberWithSpaces(
                  card.cardType === 'digital' ? card.nominal.toString() : (card.nominal * card.count).toString(),
                )} ₽`,
                nominal: `${formatNumberWithSpaces(card.nominal.toString())} ₽`,
                count: card.cardType === 'physical' ? card.count : 1,
              };
            })}
            onCancel={() => onCancelBtn(item?._id, accessToken || '')}
            onPayment={() => onPaymentBtn(item?._id, accessToken || '')}
          />
        ))}
      </div>
      {isShowMoreVisible && (
        <NewButton size="medium" onClick={() => setPage((prev) => prev + 1)} addClass="order-tab__show-more">
          {t('showMore')}
        </NewButton>
      )}
    </main>
  );
};
